/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useEffect } from 'react';
import { InlineLoading } from '@carbon/react';

import { PdfViewerHeader } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/unstructured-extraction/idp-extraction-fields/pdf-viewer';
import PdfViewer from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/unstructured-extraction/idp-extraction-fields/pdf-viewer/PdfViewer';
import { idpProjectStore } from 'stores';

import * as Styled from './DocumentViewerLayout.styled';

/** @typedef {{ id: string, name: string }} Document */

/**
 * @param {{
 *   leftPanelContent: JSX.Element,
 *   document?: { id: string },
 *   allDocuments?: Document[],
 *   onDocumentSelect?: (doc: Document) => void,
 *   emptyPdfMessage?: string,
 *   isRightPanelVisible?: boolean
 * }} props
 * @returns {JSX.Element}
 */
const DocumentViewerLayout = ({
  leftPanelContent,
  document,
  allDocuments,
  onDocumentSelect,
  emptyPdfMessage,
  isRightPanelVisible = true
}) => {
  const [idpDocumentBlob, setIdpDocumentBlob] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);

  useEffect(() => {
    const loadDocument = async () => {
      if (document?.id) {
        setIsLoadingPdf(true);
        setIdpDocumentBlob(null);
        setNumberOfPages(0);
        try {
          const blob = await idpProjectStore.getIdpDocument(document.id);
          setIdpDocumentBlob(blob);
          setCurrentPage(1);
        } catch (error) {
          console.error('DocumentViewerLayout: Failed to load document for preview:', error);
          setIdpDocumentBlob(null);
        } finally {
          setIsLoadingPdf(false);
        }
      } else {
        setIdpDocumentBlob(null);
        setNumberOfPages(0);
        setIsLoadingPdf(false);
      }
    };

    loadDocument();
  }, [document]);

  const handlePdfLoadSuccess = ({ numPages }) => {
    setNumberOfPages(numPages);
  };

  const handleDocumentChangeFromHeader = (selectedDoc) => {
    if (onDocumentSelect && selectedDoc.id !== document?.id) {
      onDocumentSelect(selectedDoc);
    }
  };

  const defaultEmptyPdfMessage = document?.id ? 'Failed to load PDF.' : 'No document selected for preview.';

  return (
    <Styled.Container>
      {/* @ts-ignore TS2339 */}
      <Styled.LeftPanel $isFullWidth={!isRightPanelVisible}>{leftPanelContent}</Styled.LeftPanel>
      {isRightPanelVisible && (
        <Styled.RightPanel>
          {document && allDocuments && allDocuments.length > 0 && (
            <PdfViewerHeader
              documents={allDocuments}
              selectedDocument={document}
              selectedDocumentPageCount={numberOfPages}
              onDocumentChange={handleDocumentChangeFromHeader}
              onDocumentPageChange={setCurrentPage}
              onDocumentScaleChange={setPdfScale}
            />
          )}
          {isLoadingPdf ? (
            <Styled.PdfWrapper>
              <InlineLoading description="Loading PDF preview..." />
            </Styled.PdfWrapper>
          ) : idpDocumentBlob ? (
            <PdfViewer
              file={idpDocumentBlob}
              pageNumber={currentPage}
              scale={pdfScale}
              onDocumentLoadSuccess={handlePdfLoadSuccess}
              onPageChange={setCurrentPage}
            />
          ) : (
            <Styled.PdfWrapper>
              <p>{emptyPdfMessage || defaultEmptyPdfMessage}</p>
            </Styled.PdfWrapper>
          )}
        </Styled.RightPanel>
      )}
    </Styled.Container>
  );
};

export { DocumentViewerLayout };
